import { createTheme } from '@mui/material/styles';

// ─────────────────────────────────────────────────────────────
// Al Amen — 2026 AI-company design system
//
// Visual language (from 2026 AI company trends):
//   • Warm off-white / cream background (Anthropic-style)
//   • Dark mode that is actually dark (near-black)
//   • Editorial palette: sand, rust, sage, slate, ink
//   • Serif display + sans body + mono code
//   • Thin borders, generous whitespace, print-inspired layout
// ─────────────────────────────────────────────────────────────

export const palette = {
  light: {
    // Warm off-white (#FAF9F5 range — Anthropic-style)
    bg: '#FAF8F2',
    surface: '#FFFFFF',
    subtle: '#F4F1E8',
    ink: '#141413',
    inkMuted: '#5B5954',
    inkSoft: '#8A8680',
    border: 'rgba(20,20,19,0.10)',
    borderStrong: 'rgba(20,20,19,0.20)',
    accent: '#C96342', // warm rust (Anthropic accent family)
    accentSoft: 'rgba(201,99,66,0.10)',
    sage: '#6B7F5C',
    slate: '#3F4A52'
  },
  dark: {
    bg: '#0A0A0A',
    surface: '#111111',
    subtle: '#161616',
    ink: '#F5F3EE',
    inkMuted: '#B4B1AA',
    inkSoft: '#7A7771',
    border: 'rgba(245,243,238,0.10)',
    borderStrong: 'rgba(245,243,238,0.20)',
    accent: '#E08463',
    accentSoft: 'rgba(224,132,99,0.14)',
    sage: '#8FA77E',
    slate: '#9AA5AD'
  }
};

export const fonts = {
  serif:
    '"Instrument Serif", "Tiempos Headline", "Cormorant Garamond", ui-serif, Georgia, serif',
  sans:
    '"Inter", "Söhne", system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  mono:
    '"JetBrains Mono", "Berkeley Mono", ui-monospace, "SFMono-Regular", Menlo, Consolas, monospace'
};

export function makeTheme({ direction = 'ltr', mode = 'light' } = {}) {
  const c = palette[mode] || palette.light;

  return createTheme({
    direction,
    palette: {
      mode,
      primary: { main: c.accent, contrastText: mode === 'dark' ? '#0A0A0A' : '#FFFFFF' },
      secondary: { main: c.slate },
      text: {
        primary: c.ink,
        secondary: c.inkMuted,
        disabled: c.inkSoft
      },
      background: {
        default: c.bg,
        paper: c.surface
      },
      divider: c.border
    },
    shape: { borderRadius: 6 },
    typography: {
      fontFamily: fonts.sans,
      // Serif display type for marketing headings
      h1: {
        fontFamily: fonts.serif,
        fontWeight: 400,
        letterSpacing: '-0.02em',
        lineHeight: 1.02,
        fontSize: 'clamp(3rem, 8vw, 6.5rem)'
      },
      h2: {
        fontFamily: fonts.serif,
        fontWeight: 400,
        letterSpacing: '-0.02em',
        lineHeight: 1.05,
        fontSize: 'clamp(2.25rem, 5vw, 4rem)'
      },
      h3: {
        fontFamily: fonts.serif,
        fontWeight: 400,
        letterSpacing: '-0.02em',
        lineHeight: 1.1,
        fontSize: 'clamp(1.875rem, 3.6vw, 2.75rem)'
      },
      h4: {
        fontFamily: fonts.serif,
        fontWeight: 500,
        letterSpacing: '-0.01em',
        lineHeight: 1.18,
        fontSize: 'clamp(1.5rem, 2.6vw, 2rem)'
      },
      h5: {
        fontFamily: fonts.sans,
        fontWeight: 600,
        letterSpacing: '-0.01em'
      },
      h6: {
        fontFamily: fonts.sans,
        fontWeight: 600,
        letterSpacing: '-0.005em'
      },
      subtitle1: { fontFamily: fonts.sans, letterSpacing: 0 },
      subtitle2: { fontFamily: fonts.sans, letterSpacing: 0 },
      body1: { fontFamily: fonts.sans, lineHeight: 1.6 },
      body2: { fontFamily: fonts.sans, lineHeight: 1.55 },
      button: {
        fontFamily: fonts.sans,
        fontWeight: 500,
        textTransform: 'none',
        letterSpacing: 0
      },
      overline: {
        fontFamily: fonts.mono,
        textTransform: 'uppercase',
        letterSpacing: '0.12em',
        fontSize: '0.72rem'
      },
      caption: { fontFamily: fonts.mono, letterSpacing: '0.02em' }
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: c.bg,
            color: c.ink,
            fontFeatureSettings: '"ss01", "cv11"',
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale'
          },
          '::selection': {
            background: c.accent,
            color: mode === 'dark' ? '#0A0A0A' : '#FFFFFF'
          }
        }
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 999,
            paddingInline: '1.1rem',
            paddingBlock: '0.55rem',
            fontWeight: 500,
            boxShadow: 'none'
          },
          contained: {
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' }
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: 'none' }
        }
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontFamily: fonts.mono,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            fontSize: '0.7rem',
            borderRadius: 6
          }
        }
      },
      MuiDivider: {
        styleOverrides: {
          root: { borderColor: c.border }
        }
      }
    }
  });
}
